<template>
  <div class="donut-chart" :class="cssClasses" :style="cssVars">
    <!-- SVG defs for the clip-path to clip the segments
           so that there won't be overlaps of the two segments when hovered -->
    <svg v-show="shouldShowClipPath" width="0" height="0">
      <defs>
        <clipPath id="primarySegmentClip">
          <path :d="clipPathData[0]"></path>
        </clipPath>
        <clipPath id="secondarySegmentClip">
          <path :d="clipPathData[1]"></path>
        </clipPath>
      </defs>
    </svg>
    <div class="donut-diagram">
      <svg class="donut" viewBox="0 0 40 40">
        <!-- Uncomment to see the clipPath visually on the donut chart -->
        <!-- <path :d="clipPathData[0]"></path>
        <path fill="blue" :d="clipPathData[1]"></path> -->
        <circle class="donut-hole" cx="50%" cy="50%" r="16"></circle>
        <circle class="donut-ring" cx="50%" cy="50%" r="16"></circle>
        <circle v-if="isLoading" class="donut-segment loader" cx="50%" cy="50%" r="16"></circle>
        <circle
          v-if="showSecondarySegment && !isLoading"
          class="donut-segment secondary"
          cx="50%"
          cy="50%"
          r="16"
          @mouseenter="showTooltip(1)"
          @mousemove="positionTooltip"
          @mouseleave="hideTooltip"
          @touchstart="showTooltip(1)"
        ></circle>
        <circle
          v-if="showPrimarySegment && !isLoading"
          class="donut-segment primary"
          cx="50%"
          cy="50%"
          r="16"
          @mouseenter="showTooltip(0)"
          @mousemove="positionTooltip"
          @mouseleave="hideTooltip"
          @touchstart="showTooltip(0)"
        ></circle>
      </svg>
      <div class="donut-info">
        <cv-skeleton-text v-if="isLoading" paragraph :line-count="2" width="90%" />
        <div v-else class="donut-info-stats">
          <span class="current"> {{ currentDisplay }}</span>
          <span class="label"> {{ t("donut_chart_out_of_label") }} </span>
          <span class="total"> {{ totalDisplay }}</span>
        </div>
      </div>

      <!-- Cannot use cv-tooltip for svg elements, hence using a div with the specified class for Carbon tooltip -->
      <div
        v-show="displayTooltip"
        class="donut-tooltip bx--tooltip"
        data-floating-menu-direction="top"
      >
        <span class="bx--tooltip__caret" />
        <div class="bx--tooltip__content">
          {{
            t("donut_chart_tooltip_score_label", {
              current: currentValueByIndex,
              total: totalValueByIndex,
            })
          }}
        </div>
      </div>
    </div>
    <ul v-if="showLegend" class="legend">
      <li
        v-for="(dataValue, index) in data"
        :key="index"
        class="current"
        :class="{
          subjectKey: dataValue.subjectKey,
        }"
      >
        <span> {{ dataValue.label }} </span>
      </li>
      <li class="total">
        <span> {{ t("donut_chart_total_marks_label") }} </span>
      </li>
    </ul>

    <div v-if="showAverage" class="average">
      <span class="label"> {{ t("donut_chart_average_label") }} </span>
      <span class="value">
        {{ t("donut_chart_average_score_label", { currentPerc }) }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import DOMUtils from "@utils/dom.utils";
import ColorUtils from "@utils/color.utils.ts";
import GeometryUtils from "@utils/geometry.utils.ts";
import subjectColorMap from "@configs/subjectColorMap";
import { SELF_STUDY_COLOR } from "@modules/content-mastery/service/content.mastery.constant";
import { isNull } from "@utils/var.check";
import { useVm } from "@utils/vm.ts";

// Types
interface DataSet {
  totalScore: number;
  totalMark: number;
  label: string;
  subjectKey?: string;
}

// Props
interface Props {
  data?: DataSet[];
  isLoading?: boolean;
  showAverage?: boolean;
  showLegend?: boolean;
}

const {
  data = [],
  isLoading = false,
  showAverage = true,
  showLegend = true,
} = defineProps<Props>();

// Composables
const { vm } = useVm();
const { t } = useI18n();

// Reactive data
const tooltipShownIndex = ref<number | null>(null);

// Computed properties
const displayTooltip = computed(() => !isNull(tooltipShownIndex.value));

const overallTotalScore = computed(() => {
  let value = 0;
  data.forEach((dataSet) => {
    value += dataSet.totalScore;
  });
  return value;
});

const overallTotalMark = computed(() => {
  let value = 0;
  data.forEach((dataSet) => {
    value += dataSet.totalMark;
  });
  return value;
});

const currentPerc = computed(() => {
  return overallTotalMark.value === 0
    ? 0
    : Math.min(Number(((overallTotalScore.value / overallTotalMark.value) * 100).toFixed(1)), 100);
});

const currentPrimaryPerc = computed(() => {
  if (isNull(data) || isNull(data[0]) || overallTotalMark.value === 0) {
    return 0;
  }
  return !data[0]
    ? 0
    : Math.min(Number(((data[0].totalScore / overallTotalMark.value) * 100).toFixed(1)), 100);
});

const currentSecondaryPerc = computed(() => {
  if (isNull(data) || isNull(data[1]) || overallTotalMark.value === 0) {
    return 0;
  }
  return !data[1]
    ? 0
    : Math.min(Number(((data[1].totalScore / overallTotalMark.value) * 100).toFixed(1)), 100);
});

const currentValueByIndex = computed(() => {
  return displayTooltip.value ? data[tooltipShownIndex.value!]?.totalScore || 0 : 0;
});

const totalValueByIndex = computed(() => {
  return displayTooltip.value ? data[tooltipShownIndex.value!]?.totalMark || 0 : 0;
});

const currentDisplay = computed(() => {
  return overallTotalScore.value.toFixed(1);
});

const totalDisplay = computed(() => {
  return overallTotalMark.value.toFixed(1);
});

const showPrimarySegment = computed(() => {
  return data.length > 0 && data[0] && data[0].totalScore > 0;
});

const showSecondarySegment = computed(() => {
  return data.length > 1 && data[1] && data[1].totalScore > 0;
});

const cssClasses = computed(() => ({
  loading: isLoading,
  "_is-all": data.length === 0 || !data[0]?.subjectKey,
  "show-clip-path": shouldShowClipPath.value,
}));

const cssVars = computed(() => {
  const primaryColor = data[0]?.subjectKey
    ? subjectColorMap[data[0].subjectKey] || SELF_STUDY_COLOR
    : SELF_STUDY_COLOR;

  const secondaryColor = data[1]?.subjectKey
    ? subjectColorMap[data[1].subjectKey] || SELF_STUDY_COLOR
    : SELF_STUDY_COLOR;

  // Calculate stroke dash arrays for the donut segments
  const primaryStrokeDashArray = (currentPrimaryPerc.value / 100) * 100;
  const primaryStrokeDashArrayEnd = 100 - primaryStrokeDashArray;

  const secondaryStrokeDashArray = (currentSecondaryPerc.value / 100) * 100;
  const secondaryStrokeDashArrayEnd = 100 - secondaryStrokeDashArray;
  const secondaryStrokeDashOffset = -primaryStrokeDashArray;

  return {
    "--primaryColor": primaryColor,
    "--tinted-primary-color": ColorUtils.getTintedColor(primaryColor, 10),
    "--secondaryColor": secondaryColor,
    "--strokeDashArrayPrimaryStart": primaryStrokeDashArray,
    "--strokeDashArrayPrimaryEnd": primaryStrokeDashArrayEnd,
    "--strokeDashArraySecondaryStart": secondaryStrokeDashArray,
    "--strokeDashArraySecondaryEnd": secondaryStrokeDashArrayEnd,
    "--strokeDashArraySecondaryOffset": secondaryStrokeDashOffset,
  };
});

const clipPathData = computed(() => {
  // Calculate angles for the segments
  const anglePrimaryDeg = (currentPrimaryPerc.value / 100) * 360;
  const angleSecondaryDeg = (currentSecondaryPerc.value / 100) * 360;
  const anglePrimaryDegWithOffset = anglePrimaryDeg + 90; // Offset to start from top

  // SVG circle parameters
  const circleCenter = 20;

  let primarySegmentArcPath = `A 16,16 0 0 0 ${circleCenter + 5} 0 Z`;
  const secondarySegmentArcPath = `A 16,16 0 0 0 ${circleCenter + 5} 0 Z`;

  // Determine quadrant and calculate coordinates
  let primarySegmentCoor = { x: 0, y: 0 };
  let secondarySegmentCoor = { x: 0, y: 0 };

  if (anglePrimaryDegWithOffset <= 90) {
    primarySegmentCoor = getClipPathSegmentColor(anglePrimaryDegWithOffset, 1, true);
    secondarySegmentCoor = getClipPathSegmentColor(angleSecondaryDeg, 1, false);
  } else if (anglePrimaryDegWithOffset <= 180) {
    primarySegmentCoor = getClipPathSegmentColor(anglePrimaryDegWithOffset, 2, true);
    secondarySegmentCoor = getClipPathSegmentColor(angleSecondaryDeg, 2, false);
  } else if (anglePrimaryDegWithOffset <= 270) {
    primarySegmentArcPath = `A 20,20 0 1 0 ${circleCenter - 5} 0 Z`;
    primarySegmentCoor = getClipPathSegmentColor(anglePrimaryDegWithOffset, 3, true);
    secondarySegmentCoor = getClipPathSegmentColor(angleSecondaryDeg, 3, false);
  } else {
    primarySegmentArcPath = `A 25,20 0 1 0 ${circleCenter - 5} 0 Z`;
    primarySegmentCoor = getClipPathSegmentColor(anglePrimaryDegWithOffset, 4, true);
    secondarySegmentCoor = getClipPathSegmentColor(angleSecondaryDeg, 4, false);
  }

  const primarySegmentClipPath = `
    M ${circleCenter},${circleCenter}
    L${primarySegmentCoor.x.toFixed(2)}, ${primarySegmentCoor.y.toFixed(2)},
    ${primarySegmentArcPath}`;

  const secondarySegmentClipPath = `
    M ${circleCenter},${circleCenter}
    L${secondarySegmentCoor.x.toFixed(2)}, ${secondarySegmentCoor.y.toFixed(2)},
    ${secondarySegmentArcPath}`;

  return [primarySegmentClipPath, secondarySegmentClipPath];
});

const shouldShowClipPath = computed(() => {
  return (
    (showPrimarySegment.value || showSecondarySegment.value) &&
    (clipPathData.value[0] !== "" || clipPathData.value[1] !== "")
  );
});

// Methods
const showTooltip = (index: number) => {
  tooltipShownIndex.value = index;
};

const hideTooltip = () => {
  tooltipShownIndex.value = null;
};

const positionTooltip = (event: MouseEvent) => {
  const xOffset = 35;
  const yOffset = 40;
  const tooltipMinWidth = 96;

  const tooltip = DOMUtils.elOf(vm.$el, ".donut-tooltip")[0];
  if (!tooltip) return;

  const tooltipWidth = tooltip.clientWidth;
  let tooltipPosLeft =
    event.clientX - (event.target as Element).getBoundingClientRect().left - xOffset;

  // Offset to align the tooltip caret correctly when it exceeds the min width
  if (tooltipWidth > tooltipMinWidth) {
    const tooltipOffset = (tooltipWidth - tooltipMinWidth) / 2;
    tooltipPosLeft = tooltipPosLeft - tooltipOffset;
  }

  Object.assign(tooltip.style, {
    left: `${tooltipPosLeft}px`,
    top: `${event.clientY - (event.target as Element).getBoundingClientRect().top - yOffset}px`,
  });
};

const getClipPathSegmentColor = (angle: number, quadrantNum: number, isPrimarySegment: boolean) => {
  const circleCenter = 20;
  const circleRadius = 16;

  let xOffSet = 0;
  let yOffSet = 0;
  let angleRad = 0;

  switch (quadrantNum) {
    case 1:
      if (isPrimarySegment) {
        xOffSet = 4;
        yOffSet = 0.5;
      } else {
        yOffSet = -4;
      }
      angleRad = GeometryUtils.degreeToRadian(90 - angle);
      return {
        x: circleCenter + Math.cos(angleRad) * circleRadius + xOffSet,
        y: circleCenter - Math.sin(angleRad) * circleRadius + yOffSet,
      };
    case 2:
      if (isPrimarySegment) {
        xOffSet = -2;
        yOffSet = 4.5;
      } else {
        xOffSet = 2;
      }
      angleRad = GeometryUtils.degreeToRadian(180 - angle);
      return {
        x: circleCenter + Math.sin(angleRad) * circleRadius + xOffSet,
        y: circleCenter + Math.cos(angleRad) * circleRadius + yOffSet,
      };
    case 3:
      if (isPrimarySegment) {
        xOffSet = -6;
        yOffSet = 1;
        if (angle > 250) {
          yOffSet = -2;
        }
      }
      angleRad = GeometryUtils.degreeToRadian(angle - 180);
      return {
        x: circleCenter - Math.sin(angleRad) * circleRadius + xOffSet,
        y: circleCenter + Math.cos(angleRad) * circleRadius + yOffSet,
      };
    case 4:
      if (isPrimarySegment) {
        yOffSet = -6;
        if (angle < 310) {
          xOffSet = -4;
        }
      }
      angleRad = GeometryUtils.degreeToRadian(360 - angle);
      return {
        x: circleCenter - Math.sin(angleRad) * circleRadius + xOffSet,
        y: circleCenter - Math.cos(angleRad) * circleRadius + yOffSet,
      };
    default:
      return { x: 0, y: 0 };
  }
};
</script>

<style lang="scss">
@import "carbon-components/scss/globals/scss/motion.scss";

@import "@styles/abstracts/functions";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

$donut-chart-stroke-width: 5;
$donut-chart-stroke-offset: 25;

.donut-chart {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  margin: 0 auto;

  @keyframes donutLoading {
    0% {
      stroke-dasharray: 0 100;
      stroke-dashoffset: 25;
    }
    25% {
      stroke-dasharray: 100 0;
      stroke-dashoffset: 25;
    }
    50% {
      stroke-dasharray: 0 100;
      stroke-dashoffset: -75;
    }
    75% {
      stroke-dasharray: 100 0;
      stroke-dashoffset: 25;
    }
    100% {
      stroke-dasharray: 0 100;
      stroke-dashoffset: 25;
    }
  }

  &.loading {
    .donut-ring {
      stroke: $ui-03;
    }

    .loader {
      stroke: $ui-04;
      animation: donutLoading 3s $duration--moderate-02 infinite;
    }
  }

  &._is-all {
    .average {
      .value {
        background-color: tint($ui-05, 90%);
        color: $ui-05;
      }
    }
  }

  &.show-clip-path {
    .donut-segment {
      &.primary {
        clip-path: url("#primarySegmentClip");
      }

      &.secondary {
        clip-path: url("#secondarySegmentClip");
      }
    }
  }

  .average {
    margin: $spacing-03 0;
    justify-self: center;
    grid-column: 6/8;

    .label {
      @include carbon--type-style("label-03", true);
      color: $text-02;
      text-transform: uppercase;
    }

    .value {
      @include carbon--type-style("productive-heading-02");
      margin-left: $spacing-03;
      padding: $spacing-01 $spacing-03;
      color: var(--primaryColor);
      background-color: var(--tinted-primary-color);
      border-radius: 5px;
    }
  }

  .cv-tab[id="selfStudy"] & {
    .average {
      .value {
        background-color: tint($interactive-01, 90%);
        color: $interactive-01;
      }
    }
  }

  .donut-diagram {
    width: 200px;
    height: 200px;
    position: relative;
    grid-column: 6/8;
  }

  .donut {
    width: 100%;
    height: 100%;
  }

  .donut-hole {
    fill: transparent;
  }

  .donut-ring {
    stroke: $ui-04;
    stroke-width: $donut-chart-stroke-width;
    fill: transparent;
  }

  .donut-segment {
    transform-origin: center;
    stroke: var(--primaryColor);
    stroke-width: $donut-chart-stroke-width;
    stroke-dasharray: var(--strokeDashArrayPrimaryStart), var(--strokeDashArrayPrimaryEnd);
    stroke-dashoffset: $donut-chart-stroke-offset;
    stroke-linecap: round;
    fill: transparent;

    &.secondary {
      stroke: var(--secondaryColor);
      stroke-dasharray: var(--strokeDashArraySecondaryStart), var(--strokeDashArraySecondaryEnd);
      stroke-dashoffset: var(--strokeDashArraySecondaryOffset);
    }
  }

  .donut-info {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    height: 6.5rem;
    width: 6.5rem;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 100%;

    .donut-info-stats {
      display: flex;
      flex-flow: column;
      justify-content: center;
      align-items: center;
    }

    .bx--skeleton__text {
      margin-left: $spacing-03;

      &:not(:last-child) {
        margin-top: $spacing-03;
      }
    }

    span {
      @include carbon--type-style("productive-heading-04");
    }

    .label {
      @include carbon--type-style("caption-01");
      font-weight: 500;
      margin: $spacing-01 0;
    }

    .label,
    .total {
      color: $text-02;
    }
  }

  .donut-tooltip {
    position: absolute;
    display: block;
    text-align: center;
    min-width: 6rem;
    white-space: nowrap;
  }

  .legend {
    display: flex;
    flex-flow: column;
    justify-content: center;
    grid-row-gap: $spacing-03;
    grid-column: 8/10;
    grid-row: 1/2;
    margin-left: $spacing-07;

    li {
      @include carbon--type-style("card-label-01", true);
      display: flex;
      align-items: center;
      color: $text-03;

      &:before {
        content: "";
        display: inline-block;
        width: 1rem;
        min-width: 1rem;
        height: 1rem;
        border-radius: 100%;
        margin-right: $spacing-04;
      }

      &.current {
        &:before {
          background-color: $inverse-link;
        }

        &.subjectKey {
          &:before {
            background-color: var(--primaryColor);
          }
        }
      }

      &.sdl {
        &:before {
          background-color: $inverse-link;
        }
      }

      &.total {
        &:before {
          background-color: $ui-04;
        }
      }
    }
  }
}
</style>
